import React, { useState, useMemo } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useQuery } from "@tanstack/react-query";
import { GetConversationRate } from "../../../Api/report";
import { ErrorToast } from "../../../Utils/SweetAlert";
import FilterSidebar from "../../../Components/FilterSidebar";
import { FaUsers, FaChartPie } from "react-icons/fa";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ConversationRateTab = () => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        preset: "all",
        type: "",
        startDate: null,
        endDate: null,
    });

    const { data: reportData, isFetching: loading } = useQuery({
        queryKey: ["get-conversation-rate", filters],
        queryFn: () => {
            const params = {
                type: filters.type || "",
                startDate: filters.startDate || "",
                endDate: filters.endDate || "",
            };
            return GetConversationRate(params);
        },
        throwOnError: (error) => {
            ErrorToast(error?.response?.data?.message || "Server Error.");
        },
    });

    const data = reportData?.data || {};
    const totalUsersCount = data.totalUsersCount || 0;
    const conversationByAgeGroup = data.conversationByAgeGroup || [];

    const sumConversations = conversationByAgeGroup.reduce((acc, curr) => acc + curr.count, 0);
    const conversationRate = totalUsersCount > 0 ? ((sumConversations / totalUsersCount) * 100).toFixed(2) : "0.00";

    const chartData = useMemo(() => {
        return {
            labels: conversationByAgeGroup.map(item => item.label),
            datasets: [
                {
                    label: "Users",
                    data: conversationByAgeGroup.map(item => item.count),
                    backgroundColor: "#AD5ADC",
                    borderRadius: 8,
                },
            ],
        };
    }, [conversationByAgeGroup]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
            },
        },
    };

    return (
        <div className="reports-tab-content">
            <div className="row subscription-stats-row mb-4">
                <div className="col-xl-3 col-lg-6 mb-3">
                    <div className="subscription-stats-card">
                        <div className="subscription-stats-content">
                            <div className="subscription-stats-title">Total Users</div>
                            <div className="subscription-stats-value">{totalUsersCount}</div>
                        </div>
                        <div className="subscription-stats-icon">
                            <FaUsers />
                        </div>
                    </div>
                </div>

                <div className="col-xl-4 col-lg-6 mb-3">
                    <div className="subscription-stats-card">
                        <div className="subscription-stats-content">
                            <div className="subscription-stats-title">Conversation Rate</div>
                            <div className="subscription-stats-value">
                                {conversationRate}% <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#6c757d' }}>({sumConversations})</span>
                            </div>
                        </div>
                        <div className="subscription-stats-icon">
                            <FaChartPie />
                        </div>
                    </div>
                </div>

                <div className="col d-flex justify-content-end align-items-start pt-2">
                    <button
                        type="button"
                        className={`subscription-filter-trigger action-btn ${isFilterOpen ? 'active' : ''} h-auto py-2`}
                        onClick={() => setIsFilterOpen(true)}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                            <line x1="4" y1="6" x2="20" y2="6"></line>
                            <line x1="4" y1="12" x2="20" y2="12"></line>
                            <line x1="4" y1="18" x2="20" y2="18"></line>
                        </svg>
                        Filters
                    </button>
                    <FilterSidebar
                        open={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        initialFilters={filters}
                        onApply={(values) => setFilters(values)}
                        onReset={() => setFilters({ preset: "all", type: "", startDate: null, endDate: null })}
                        moduleType="reports"
                        tabType="reports"
                    />
                </div>
            </div>

            <div className="card flex-fill border-card mb-3">
                <div className="card-header dashboard-chat-heading p-2">
                    <h5 className="card-title text-black fw-bold fs-4 pl-2">Conversation By Age Group</h5>
                </div>
                <div className="card-body" style={{ height: '55vh' }}>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="spinner-border loader-theme" role="status" />
                        </div>
                    ) : (
                        <Bar options={options} data={chartData} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversationRateTab;
